import L from "leaflet";

/** Geofence helpers shared by the pickup-location card and the onboarding wizard. */

export type Geofence = {
  id: string;
  latlngs: L.LatLngExpression[];
};

// The 50m ring seeded from a 2GIS link carries this prefix: a suggestion, not the user's.
const SUGGESTED = "auto-";
const isSuggested = (fence: Geofence) => fence.id.startsWith(SUGGESTED);

/** The fence as the user's own: a suggestion they reshaped is no longer one. */
export const claimFence = (fence: Geofence): Geofence =>
  isSuggested(fence) ? { ...fence, id: fence.id.slice(SUGGESTED.length) } : fence;

/**
 * The 50m ring seeded from a fresh 2GIS link (id `auto-…`) is a suggestion: it gives
 * way once the user has drawn a fence of their own, i.e. one with three corners.
 */
export const withoutReplacedSuggestion = (fences: Geofence[]): Geofence[] =>
  fences.some((fence) => !isSuggested(fence) && fence.latlngs.length >= 3)
    ? fences.filter((fence) => !isSuggested(fence))
    : fences;

const EARTH_RADIUS_METERS = (L.CRS.Earth as { R?: number }).R || 6378137; // typically 6378137 in meters

const DEFAULT_GEOFENCE_POINTS = 32;

/** A closed ring of `[lat, lon]` pairs approximating a circle around the point. */
export const generateCircleGeofence = (
  centerLat: number,
  centerLon: number,
  radiusInMeters: number,
  points = DEFAULT_GEOFENCE_POINTS,
): [number, number][] => {
  const coords: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points;
    const piAngle = (angle * Math.PI) / 180;
    const dy = radiusInMeters * Math.sin(piAngle);
    const dx = radiusInMeters * Math.cos(piAngle);
    const lat = centerLat + (dy / EARTH_RADIUS_METERS) * (180 / Math.PI);
    const lon =
      centerLon +
      ((dx / EARTH_RADIUS_METERS) * (180 / Math.PI)) / Math.cos((centerLat * Math.PI) / 180);
    coords.push([lat, lon]);
  }
  // Ensure the polygon ring is explicitly closed by repeating the first coordinate.
  if (coords.length > 0) {
    coords.push(coords[0]);
  }
  return coords;
};

type FenceLike = { latlngs?: unknown[] };

/** Drawn fences as the proto's GeoJSON `geometry`: one fence stays a Polygon for legacy readers, several become a MultiPolygon. */
export const buildGrpcGeometry = (geofences: FenceLike[] | undefined) => {
  const rings = (geofences ?? [])
    .map((fence) => {
      const coordinates = (fence.latlngs || [])
        .map((latlng: any) => {
          if (Array.isArray(latlng) && latlng.length === 2) return [latlng[1], latlng[0]];
          if (latlng && typeof latlng.lat === "number" && typeof latlng.lng === "number")
            return [latlng.lng, latlng.lat];
          return null;
        })
        .filter((c): c is [number, number] => Array.isArray(c) && c.length === 2);
      if (
        coordinates.length &&
        (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
          coordinates[0][1] !== coordinates[coordinates.length - 1][1])
      ) {
        coordinates.push(coordinates[0]);
      }
      return coordinates;
    })
    // A closed ring needs three corners plus the closing point: a fence saved
    // mid-drawing, with fewer, encloses nothing and would be invalid GeoJSON.
    .filter((ring) => ring.length >= 4);

  if (rings.length === 0) return undefined;

  return {
    type: "Feature",
    properties: {},
    geometry:
      rings.length === 1
        ? { type: "Polygon", coordinates: [rings[0]] }
        : { type: "MultiPolygon", coordinates: rings.map((ring) => [ring]) },
  };
};
